import React, { useState } from 'react';
import { BattleState, BattleUnitState, HotbarSkill, BattleItem } from '../types/battle';
import {
  getCurrentTurnUnit,
  calculateValidTargets,
  calculateWeaponRange,
  calculateSkillNodeTargets,
} from '../utils/battleUtils';
import { makeAuthenticatedRequest } from '../utils/api';
import { logger } from '../utils/logger';

export function useBattleModes(
  battle: BattleState | null,
  selectedUnit: BattleUnitState | null,
  battleId: number,
  setSelectedUnit: React.Dispatch<React.SetStateAction<BattleUnitState | null>>,
  addEnhancedBattleLog: (type: string, data: Record<string, unknown>) => void,
  setActionError: (msg: string | null) => void,
  executeSkillDirectFn: (skill: HotbarSkill, x: number, y: number) => Promise<void>
) {
  const [movementMode, setMovementMode] = useState(false);
  const [attackMode, setAttackMode] = useState(false);
  const [skillMode, setSkillMode] = useState(false);
  const [validMoves, setValidMoves] = useState<{ x: number; y: number }[]>([]);
  const [validTargets, setValidTargets] = useState<BattleUnitState[]>([]);
  const [weaponRange, setWeaponRange] = useState<{ x: number; y: number }[]>([]);
  const [skillTargets, setSkillTargets] = useState<{ x: number; y: number }[]>([]);
  const [skillRange, setSkillRange] = useState<{ x: number; y: number }[]>([]);
  const [aoeArea, setAoeArea] = useState<{ x: number; y: number }[]>([]);
  const [selectedSkill, setSelectedSkill] = useState<HotbarSkill | null>(null);
  const [itemMode, setItemMode] = useState(false);
  const [selectedItem, setSelectedItem] = useState<BattleItem | null>(null);
  const [itemTargets, setItemTargets] = useState<BattleUnitState[]>([]);

  const clearAllModes = () => {
    setMovementMode(false);
    setAttackMode(false);
    setSkillMode(false);
    setValidMoves([]);
    setValidTargets([]);
    setWeaponRange([]);
    setSkillTargets([]);
    setSkillRange([]);
    setAoeArea([]);
    setSelectedSkill(null);
    setItemMode(false);
    setSelectedItem(null);
    setItemTargets([]);
  };

  const clearSkillState = () => {
    setSkillMode(false);
    setSelectedSkill(null);
    setSkillRange([]);
    setSkillTargets([]);
    setAoeArea([]);
  };

  const clearSelectionState = (newUnit: BattleUnitState | null = null) => {
    setSelectedUnit(newUnit);
    clearAllModes();
  };

  const calculateValidMoves = async (unit: BattleUnitState): Promise<{ x: number; y: number }[]> => {
    if (!unit || !unit.isPlayerUnit || unit.stats.currentMovement <= 0) return [];
    try {
      const response = await makeAuthenticatedRequest(`/battles/${battleId}/valid-moves/${unit.id}`, { method: 'GET' });
      if (response.ok) {
        const result = await response.json();
        return result.validMoves.map((move: { x: number; y: number }) => ({ x: move.x, y: move.y }));
      }
      logger.error('Failed to fetch valid moves');
      return [];
    } catch (error) {
      logger.error('Error calculating valid moves:', error);
      return [];
    }
  };

  const enterMovementMode = async () => {
    if (!selectedUnit || !selectedUnit.isPlayerUnit || selectedUnit.stats.currentMovement <= 0) return;
    const currentTurnUnit = getCurrentTurnUnit(battle);
    if (currentTurnUnit && currentTurnUnit.unitId === selectedUnit.id) {
      setMovementMode(true);
      const moves = await calculateValidMoves(selectedUnit);
      setValidMoves(moves);
      setActionError(null);
    } else {
      addEnhancedBattleLog('error', {
        message: `❌ Cannot move ${selectedUnit.name} - it's ${currentTurnUnit?.name || 'another unit'}'s turn!`,
      });
      setActionError(
        `It's ${currentTurnUnit?.name || 'another unit'}'s turn. You can only move the active unit.`
      );
    }
  };

  const enterAttackMode = () => {
    if (!selectedUnit || !selectedUnit.isPlayerUnit || selectedUnit.stats.currentAP <= 0) return;
    const currentTurnUnit = getCurrentTurnUnit(battle);
    if (currentTurnUnit && currentTurnUnit.unitId === selectedUnit.id) {
      logger.debug('🔍 Selected unit full data:', selectedUnit);
      logger.debug('🔍 Equipment field:', selectedUnit.equipment);
      logger.debug('🔍 All unit keys:', Object.keys(selectedUnit));

      setSkillMode(false);
      setSkillRange([]);
      setSkillTargets([]);
      setSelectedSkill(null);
      setAttackMode(true);
      setValidTargets(calculateValidTargets(selectedUnit, battle?.units ?? []));
      setWeaponRange(battle ? calculateWeaponRange(selectedUnit, battle) : []);
      setActionError(null);
    } else {
      addEnhancedBattleLog('error', {
        message: `❌ Cannot attack with ${selectedUnit.name} - it's ${currentTurnUnit?.name || 'another unit'}'s turn!`,
      });
      setActionError(
        `It's ${currentTurnUnit?.name || 'another unit'}'s turn. You can only attack with the active unit.`
      );
    }
  };

  const cancelMovement = () => {
    setMovementMode(false);
    setValidMoves([]);
  };

  const cancelAttack = () => {
    setAttackMode(false);
    setValidTargets([]);
    setWeaponRange([]);
  };

  const cancelSkill = () => {
    setSkillMode(false);
    setSelectedSkill(null);
    setSkillTargets([]);
    setSkillRange([]);
    setAoeArea([]);
  };

  const selectSkillNode = (skill: HotbarSkill) => {
    const selfTargetSkills = ['Stealth', 'Camouflage', 'Berserker Rage', 'Blessing', 'Divine Protection'];
    if (selfTargetSkills.includes(skill.skill.name)) {
      if (selectedUnit) {
        executeSkillDirectFn(skill, selectedUnit.position.x, selectedUnit.position.y);
      }
      return;
    }
    setAttackMode(false);
    setWeaponRange([]);
    setValidTargets([]);
    setMovementMode(false);
    setValidMoves([]);
    setSelectedSkill(skill);
    setSkillMode(true);
    if (selectedUnit && battle) {
      const { targets, rangePositions } = calculateSkillNodeTargets(skill, selectedUnit, battle);
      setSkillTargets(targets);
      setSkillRange(rangePositions);
    }
  };

  const cancelSkillNode = () => {
    setSkillMode(false);
    setSelectedSkill(null);
    setSkillTargets([]);
    setSkillRange([]);
  };

  // Targeting for v1 is deliberately simple: self or an adjacent (Manhattan distance <= 1) ally.
  const selectBattleItem = (item: BattleItem) => {
    if (!selectedUnit || !battle) return;
    const currentTurnUnit = getCurrentTurnUnit(battle);
    if (!currentTurnUnit || currentTurnUnit.unitId !== selectedUnit.id) {
      setActionError(`It's ${currentTurnUnit?.name || 'another unit'}'s turn. You can only use items with the active unit.`);
      return;
    }
    setAttackMode(false);
    setWeaponRange([]);
    setValidTargets([]);
    setMovementMode(false);
    setValidMoves([]);
    setSkillMode(false);
    setSelectedSkill(null);
    setSkillTargets([]);
    setSkillRange([]);
    setSelectedItem(item);
    setItemMode(true);
    const targets = (battle.units ?? []).filter(u =>
      u.isPlayerUnit &&
      u.stats.currentHP > 0 &&
      Math.abs(u.position.x - selectedUnit.position.x) + Math.abs(u.position.y - selectedUnit.position.y) <= 1
    );
    setItemTargets(targets);
    setActionError(null);
  };

  const cancelItem = () => {
    setItemMode(false);
    setSelectedItem(null);
    setItemTargets([]);
  };

  return {
    // State for rendering
    movementMode, attackMode, skillMode,
    validMoves, validTargets, weaponRange,
    skillTargets, skillRange, aoeArea, selectedSkill,
    itemMode, selectedItem, itemTargets,
    // Raw setters for action handlers that need granular control
    setMovementMode, setAttackMode, setSkillMode,
    setValidMoves, setValidTargets, setWeaponRange,
    setSkillTargets, setSkillRange, setAoeArea, setSelectedSkill,
    setItemMode, setSelectedItem, setItemTargets,
    // Convenience functions
    clearAllModes, clearSkillState, clearSelectionState,
    enterMovementMode, enterAttackMode,
    cancelMovement, cancelAttack, cancelSkill,
    selectSkillNode, cancelSkillNode,
    selectBattleItem, cancelItem,
  };
}
